import { Datatype } from '@framework/engine';
import { ICompanyApplicantData } from './CompanyApplicantData';

export interface IApplicantDataType {
  code: string;
  label: string;
  description?: string;
  type: Datatype;
}

export function createApplicantDataType(
  code: string,
  config: Omit<IApplicantDataType, 'code'>,
): IApplicantDataType {
  return {
    code,
    ...config,
  };
}

const ApplicantDataTypesAll = {
  name: createApplicantDataType('name', {
    label: 'Nome',
    type: Datatype.String,
  }),
  familyName: createApplicantDataType('familyName', {
    label: 'Cognome',
    type: Datatype.String,
  }),
  genre: createApplicantDataType('genre', {
    label: 'Genere',
    type: Datatype.String,
  }),
  fiscalNumber: createApplicantDataType('fiscalNumber', {
    label: 'Codice Fiscale',
    type: Datatype.String,
  }),
  vatNumber: createApplicantDataType('vatNumber', {
    label: 'Partita IVA',
    type: Datatype.String,
  }),
  // fiscalNumberSimple: createApplicantDataType('fiscalNumberSimple', {
  //   label: 'Codice Fiscale italiano',
  //   description: 'Codice fiscale senza prefisso TINIT-',
  //   type: Datatype.String,
  // }),
  placeOfBirth: createApplicantDataType('placeOfBirth', {
    label: 'Codice Belfiore luogo di nascita',
    description: 'Codice Belfiore del comune di nascita',
    type: Datatype.String,
  }),
  placeOfBirthName: createApplicantDataType('placeOfBirthName', {
    label: 'Comune di nascita',
    type: Datatype.String,
  }),
  countyOfBirth: createApplicantDataType('countyOfBirth', {
    label: 'Codice provincia di nascita',
    description: "Codice della provincia di nascita, ad esempio 'MI'",
    type: Datatype.String,
  }),
  dateOfBirth: createApplicantDataType('dateOfBirth', {
    label: 'Data di nascita',
    type: Datatype.Date,
  }),
  digitalAddress: createApplicantDataType('digitalAddress', {
    label: 'Indirizzo PEC',
    type: Datatype.String,
  }),
  email: createApplicantDataType('email', {
    label: 'Indirizzo email',
    type: Datatype.String,
  }),
  idCard: createApplicantDataType('idCard', {
    label: 'ID documento di identità',
    type: Datatype.String,
  }),
  idCardType: createApplicantDataType('idCardType', {
    label: 'Tipo di documento di identità',
    type: Datatype.String,
  }),
  idCardIssuer: createApplicantDataType('idCardIssuer', {
    label: 'Comune di rilascio documento di identità',
    type: Datatype.String,
  }),
  idCardIssueDate: createApplicantDataType('idCardIssueDate', {
    label: 'Data di rilascio documento di identità',
    type: Datatype.Date,
  }),
  idCardExpirationDate: createApplicantDataType('idCardExpirationDate', {
    label: 'Data di scadenza documento di identità',
    type: Datatype.Date,
  }),
  mobilePhone: createApplicantDataType('mobilePhone', {
    label: 'Numero di telefono',
    type: Datatype.String,
  }),
  domicileStreetAddress: createApplicantDataType('domicileStreetAddress', {
    label: 'Indirizzo di domicilio',
    type: Datatype.String,
  }),
  domicilePostalCode: createApplicantDataType('domicilePostalCode', {
    label: 'CAP di domicilio',
    type: Datatype.String,
  }),
  domicileMunicipality: createApplicantDataType('domicileMunicipality', {
    label: 'Comune di domicilio',
    type: Datatype.String,
  }),
  domicileProvince: createApplicantDataType('domicileProvince', {
    label: 'Provincia di domicilio',
    type: Datatype.String,
  }),
  domicileNation: createApplicantDataType('domicileNation', {
    label: 'Nazione di domicilio',
    type: Datatype.String,
  }),
  // - Indirizzo di residenza del titolare
  residenceAddress: createApplicantDataType('residenceAddress', {
    label: 'Indirizzo di residenza',
    type: Datatype.String,
  }),
  // - CAP di residenza del titolare
  residencePostalCode: createApplicantDataType('residencePostalCode', {
    label: 'CAP di residenza',
    type: Datatype.String,
  }),
  // - Comune di residenza del titolare
  residenceMunicipality: createApplicantDataType('residenceMunicipality', {
    label: 'Comune di residenza',
    type: Datatype.String,
  }),
  // - Provincia di residenza del titolare
  residenceProvince: createApplicantDataType('residenceProvince', {
    label: 'Provincia di residenza',
    type: Datatype.String,
  }),
  // - Nazione di residenza del titolare
  residenceNation: createApplicantDataType('residenceNation', {
    label: 'Nazione di residenza',
    type: Datatype.String,
  }),
  // - Numero civico di residenza (nel caso lo si voglia tenere separato dalla via)
  residenceCivicNumber: createApplicantDataType('residenceCivicNumber', {
    label: 'Numero civico di residenza',
    type: Datatype.String,
  }),
  // campo "dataScadenza" in datiAnagraficiAggiuntivi
  //(?)
  // expirationDate: createApplicantDataType('expirationDate', {
  //   label: 'Data di scadenza',
  //   description: '',
  //   type: Datatype.Date,
  // }),
  // - Sede legale
  legalHeadquarters: createApplicantDataType('legalHeadquarters', {
    label: 'Sede legale',
    type: Datatype.String,
  }),
};

type ApplicantDataTypesSchema = typeof ApplicantDataTypesAll;
export type ApplicantDataKey = keyof ApplicantDataTypesSchema;

export type IApplicantData = {
  [K in keyof ApplicantDataTypesSchema]: string | undefined;
} & {
  company: ICompanyApplicantData | undefined;
};

export class ApplicantDataTypes {
  static Map = ApplicantDataTypesAll;

  static List = Object.values(ApplicantDataTypesAll);

  static Codes = Object.keys(ApplicantDataTypesAll) as Array<
    keyof typeof ApplicantDataTypesAll
  >;

  /**
   * Ritorna true se la chiave passata è una chiave di dati del titolare.
   * (e.g. name, familyName, ...)
   */
  static isApplicantDataCode(key: string): key is ApplicantDataKey {
    return this.Codes.includes(key as ApplicantDataKey);
  }
}
